import moment from 'moment-timezone';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { getLeavetime, parseTime, parseTimeSlots } from '@utils/helpers';

import { DEFAULT_MAX_PARTY_SIZE } from '@features/tables/constants';

import Error from '@common/components/Error';
import StyledError from '@common/components/FormElements/styled/Error';
import Portal from '@common/components/Portal';
import { RHFStartDatePicker } from '@common/components/StartDatePicker';

import { Button } from '@common/components/Button';
import { Checkbox } from '@common/components/Checkbox/Checkbox';

import { RHFDatesSlider } from '@common/components/DatesSlider';

import { AlertWithChildren } from '@common/components/Alert/Alert';
import { ReadOnlyTextField } from '@common/components/FormElements';
import { RHFBlockSelectField } from '@common/components/FormElements/RHFBlockSelectField';
import { TextArea } from '@common/components/FormElements/Textarea';
import { RHFGuestPicker } from '@common/components/GuestPicker';
import { Routes } from '@common/constants';
import { usePrevious } from '@common/hooks/usePrevious';
import { useGetTableDepositValue } from '@features/tables/hooks/useGetTableDepositValue';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { useTranslations } from '@lang';
import { some } from '@utils/some';

import {
  AdditionalInfoWrapper,
  AdditionNotesWrapper,
  DepositAmount,
  DepositDescription,
  FieldSpacer,
  FlexContainer,
  FormBox,
  ReturnMessage,
  Section,
} from './TableForm.styles';
import { TableFormValues } from './TableForm.schema';
import { TableFormProps } from './TableForm.types';

const getSelectedTimeSlot = (timeSlots, time) => {
  if (!timeSlots || !time) {
    return null;
  }
  const selectedDate = timeSlots?.find(
    (timeslot) => timeslot.date === moment(time).format('YYYY-MM-DD')
  );
  const selectedTimeslot = selectedDate?.data.timeSlots.find((slot) => slot.timeSlot === time);
  return selectedTimeslot;
};

const isPromotionRequired = (timeslot) => timeslot && !timeslot.hasStandardAvailability;

export const TableForm = (props: TableFormProps): JSX.Element => {
  const {
    changeJourneyParam,
    checkAvailability,
    dates,
    fetchRestaurantConfig,
    id,
    onGuestsInputChanged,
    onGuestsInputClose,
    onGuestsInputOpen,
    onSubmit,
    promotions,
    restaurantConfig,
    restaurants = [],
    slotMax,
    slotMin,
    timeslotError,
    timeSlots,
    property,
    tablesDisableChildBookings,
  } = props;

  const { t } = useTranslations();
  const {
    control,
    setValue,
    getValues,
    register,
    formState: { isSubmitting },
  } = useFormContext<TableFormValues>();

  const restaurantValue = useWatch({ control, name: 'restaurant.value' }) as string | undefined;
  const guestsAdults = useWatch({ control, name: 'guests.adults' });
  const guestsChildren = useWatch({ control, name: 'guests.children' });
  const visitDate = useWatch({ control, name: 'visitDate' });
  const timeslot = useWatch({ control, name: 'timeslot' });
  const promotionId = useWatch({ control, name: 'promotion.id' }) as
    | string
    | number
    | undefined
    | null;
  const hasDietaryRequirements = useWatch({ control, name: 'hasDietaryRequirements' });
  const hasCelebrationOptions = useWatch({ control, name: 'hasCelebrationOptions' });

  const visitDateStr = visitDate?.format?.('YYYY-MM-DD') ?? visitDate;

  const prevRestaurantValue = usePrevious(restaurantValue);
  const prevVisitDateStr = usePrevious(visitDateStr);

  const { data: tablesPage } = useGetTablesPage(property.id, property.defaultLanguage);
  const { dietaryRequirements = [], celebrationOptions = [] } = tablesPage || {
    dietaryRequirements: [],
    celebrationOptions: [],
  };

  const selectedRestaurant = restaurants.find((rest) => {
    return rest.config.name === restaurantValue || rest.restaurantName === restaurantValue;
  });

  const isSubmitDisabled = () => {
    if (isSubmitting) return true;

    const selectedTimeslot = getSelectedTimeSlot(timeSlots, timeslot);

    if (!selectedRestaurant) return true;
    if (!timeslot || !selectedTimeslot) return true;
    if (isPromotionRequired(selectedTimeslot) && !promotionId) return true;

    return false;
  };

  useEffect(() => {
    const restaurantChanged =
      restaurantValue !== prevRestaurantValue && prevRestaurantValue !== undefined;
    const visitDateChanged = visitDateStr !== prevVisitDateStr && prevVisitDateStr !== undefined;

    if ((restaurantChanged || visitDateChanged) && restaurantValue && visitDate) {
      setValue('timeslot', '');
      fetchRestaurantConfig({ restaurant: getValues('restaurant'), visitDate });
    }
  }, [restaurantValue, visitDateStr]);

  useEffect(() => {
    const selectedTimeslot = getSelectedTimeSlot(timeSlots, timeslot);
    if (selectedTimeslot) {
      setValue('timeslotReservationId', selectedTimeslot?.timeslotReservationId);
    }
  }, [timeslot, timeSlots, setValue]);

  const getDepositPrice = (amount: number, currency = 'GBP', userLocale = 'EN-gb') => {
    return new Intl.NumberFormat(userLocale, { style: 'currency', currency })
      .formatToParts(amount / 100)
      .map((val) => val.value)
      .join('');
  };

  const visitDateFormatted = visitDate?.format?.('YYYY-MM-DD') || '';

  const depositRestaurantConfigId = visitDateFormatted
    ? selectedRestaurant?.config?.id != null
      ? String(selectedRestaurant.config.id)
      : undefined
    : undefined;

  const { data: tableDepositValue, isLoading: tableDepositValueIsLoading } =
    useGetTableDepositValue(
      property.uuid,
      parseInt(guestsAdults || '1') + parseInt(guestsChildren || '0'),
      visitDateFormatted,
      depositRestaurantConfigId ?? '',
      promotionId ? String(promotionId) : undefined
    );

  function getPromotions(promotions, timeSlot, restaurantConfig) {
    const getLabel = (promotion, filledPromotion) =>
      promotion.price > 0
        ? `${filledPromotion.name} - ${promotion.price} ${
            restaurantConfig ? restaurantConfig.currencySymbol : ''
          }`
        : filledPromotion.name;
    if (timeSlot && promotions.length) {
      return (
        timeSlot.availablePromotions?.reduce((acc, promotion) => {
          const filledPromotion = promotions.find(
            (promotionFull) => String(promotionFull.id) === String(promotion.id)
          );
          if (filledPromotion) {
            acc.push({
              value: {
                id: filledPromotion.id,
                timeslotReservationId: promotion.timeslotReservationId,
              },
              label: getLabel(promotion, filledPromotion),
            });
          }
          return acc;
        }, []) || []
      );
    }
    return [];
  }

  function getData({ inputDate = null, inputRestaurant = null }) {
    const restaurantForLookup = inputRestaurant
      ? { value: inputRestaurant, restaurantName: inputRestaurant }
      : getValues('restaurant');

    checkAvailability({
      guests: getValues('guests'),
      restaurant: restaurantForLookup,
      visitDate: inputDate || getValues('startDate'),
    });
  }

  const timeSlotOptions = parseTimeSlots(timeSlots);
  const selectedTimeslot = getSelectedTimeSlot(timeSlots, timeslot);
  const promotionOptions = getPromotions(promotions, selectedTimeslot, restaurantConfig);
  const maxBookingDate =
    restaurantConfig && restaurantConfig?.acceptBookingsDaysInAdvance > 0
      ? moment().add(restaurantConfig.acceptBookingsDaysInAdvance, 'days')
      : null;

  const leaveTime = getLeavetime(selectedTimeslot, promotionId);

  return (
    <form onSubmit={onSubmit} noValidate>
      <Section>
        <FormBox>
          {restaurants.length > 1 ? (
            <RHFBlockSelectField<TableFormValues>
              name="restaurant"
              labelWidth="150px"
              options={restaurants}
              parse={(selected) => ({
                value: selected.value,
                restaurantName: selected.restaurantName,
              })}
              format={(value, options) =>
                options.find(
                  (r) => r.value === value?.value || r.restaurantName === value?.value
                ) || null
              }
              handleAfterChange={(selected) => {
                getData({ inputRestaurant: selected.restaurantName });
                window.history.replaceState(null, '', Routes.TABLES_BOOK);
              }}
              isRequired
              label={t('tables.restaurantSelect')}
              useNativeDropdownOnMobile={false}
            />
          ) : (
            <ReadOnlyTextField
              label={t('tables.restaurantSelect')}
              displayText={restaurants?.[0]?.label}
            />
          )}
          <FieldSpacer>
            <RHFGuestPicker<TableFormValues>
              name="guests"
              required
              onAfterChange={(_type, updated) => {
                changeJourneyParam('tableAdults', Number(updated.adults));
                changeJourneyParam('tableChildren', Number(updated.children));
                onGuestsInputChanged();
                onGuestsInputOpen();
              }}
              onClose={onGuestsInputClose}
              disabledPlus={
                (slotMax ?? restaurantConfig?.maxOnlinePartySize ?? DEFAULT_MAX_PARTY_SIZE) <=
                parseInt(guestsAdults || '1') + parseInt(guestsChildren || '0')
              }
              disabledMinus={
                (slotMin ?? restaurantConfig?.minOnlinePartySize ?? 1) >=
                parseInt(guestsAdults || '1') + parseInt(guestsChildren || '0')
              }
              isSingle={tablesDisableChildBookings}
              rooms={[
                {
                  roomNumber: 1,
                  adults: parseInt(guestsAdults || '1'),
                  children: parseInt(guestsChildren || '0'),
                },
              ]}
              onRoomCountChange={() => {}}
            />
          </FieldSpacer>
          {!!timeslotError && (
            <StyledError labelWidth="150px">
              <Error error={timeslotError.partySize} />
            </StyledError>
          )}
        </FormBox>
        <FormBox>
          <RHFStartDatePicker<TableFormValues>
            name="startDate"
            pickerTextId="common.calendar.changeDateCTA"
            popperModifiers={{
              preventOverflow: {
                enabled: true,
                escapeWithReference: false,
                boundariesElement: 'viewport',
              },
            }}
            maxDate={maxBookingDate || undefined}
            handleAfterChange={(data) => {
              setValue('visitDate', undefined as any);
              getData({ inputDate: data as any });
            }}
          />
          <RHFDatesSlider<TableFormValues>
            name="visitDate"
            visitDate={visitDate}
            dates={dates || []}
            telephoneOnly={false}
            isLoading={false}
          />
          <RHFBlockSelectField<TableFormValues>
            name="timeslot"
            isRequired
            placeholder={t('product.selectPlaceholder')}
            options={timeSlotOptions.filter(
              ({ date }) => date === visitDate?.format?.('YYYY-MM-DD')
            )}
            handleAfterChange={() => {
              if (isPromotionRequired(selectedTimeslot)) {
                setValue('promotion', null);
              }
            }}
            noOptionsMessage={() => t('tables.noTimeslots')}
            label={t('tables.timeSelect')}
            useNativeDropdownOnMobile={false}
          />
          {promotionOptions && !!promotionOptions.length && (
            <FieldSpacer>
              <RHFBlockSelectField<TableFormValues>
                name="promotion"
                labelWidth="150px"
                options={promotionOptions}
                parse={(selected) => selected?.value || null}
                format={(value, options) =>
                  value?.id ? options.find((p) => p.value.id === value.id) : null
                }
                isRequired={isPromotionRequired(selectedTimeslot)}
                isClearable={!isPromotionRequired(selectedTimeslot)}
                noOptionsMessage={() => t('tables.noMenus')}
                label={t('tables.menuSelect')}
                useNativeDropdownOnMobile={false}
              />
            </FieldSpacer>
          )}
          {selectedTimeslot && selectedTimeslot.isLeaveTimeRequired && (
            <ReturnMessage>{t('tables.returnInfo', { time: parseTime(leaveTime) })}</ReturnMessage>
          )}
          {tableDepositValue &&
            selectedTimeslot &&
            !tableDepositValueIsLoading &&
            ((!!promotionOptions.length && promotionId) || !promotionOptions.length) && (
              <>
                <AlertWithChildren testId="table-deposit-alert">
                  <FlexContainer>
                    <div>
                      <DepositAmount>
                        {getDepositPrice(
                          Number(tableDepositValue.cost.amount),
                          tableDepositValue?.cost?.currency || 'gbp'
                        )}{' '}
                      </DepositAmount>
                      <DepositDescription>
                        is required to secure this booking.{' '}
                        {tableDepositValue?.description && tableDepositValue.description}
                      </DepositDescription>
                    </div>
                  </FlexContainer>
                </AlertWithChildren>
              </>
            )}
          {some(dietaryRequirements) && (
            <AdditionalInfoWrapper>
              <Checkbox
                id="dietary-requirements-checkbox"
                label={t('tables.dietaryRequirementsText')}
                {...register('hasDietaryRequirements', {
                  onChange: (e) => {
                    if (!e.target.checked) {
                      setValue('dietaryRequirements', []);
                    }
                  },
                })}
              />
              {hasDietaryRequirements && (
                <RHFBlockSelectField<TableFormValues>
                  name="dietaryRequirements"
                  id="dietary-requirements-select"
                  isMulti
                  options={dietaryRequirements.map((option) => ({
                    value: option,
                    label: option,
                  }))}
                  noOptionsMessage={() => t('tables.noDietaryRequirementOptions')}
                  useNativeDropdownOnMobile={false}
                />
              )}
            </AdditionalInfoWrapper>
          )}
          {some(celebrationOptions) && (
            <AdditionalInfoWrapper>
              <Checkbox
                id="celebration-options-checkbox"
                label={t('tables.specialOccasionText')}
                {...register('hasCelebrationOptions', {
                  onChange: (e) => {
                    if (!e.target.checked) {
                      setValue('celebrationOptions', []);
                    }
                  },
                })}
              />
              {hasCelebrationOptions && (
                <RHFBlockSelectField<TableFormValues>
                  name="celebrationOptions"
                  id="celebration-options-select"
                  isMulti
                  options={celebrationOptions.map((option) => ({
                    value: option,
                    label: option,
                  }))}
                  noOptionsMessage={() => 'No celebration options available'}
                  useNativeDropdownOnMobile={false}
                />
              )}
            </AdditionalInfoWrapper>
          )}
        </FormBox>
        <AdditionNotesWrapper>
          <TextArea
            isFullWidth
            input={register('note')}
            label={t('tables.notes')}
            id="additional-notes"
            error={undefined}
          />
        </AdditionNotesWrapper>
      </Section>
      <Portal selector={`#${id}`}>
        <Button type="button" isPrimary onClick={onSubmit} disabled={isSubmitDisabled()}>
          {t('tables.addToBasket')}
        </Button>
      </Portal>
    </form>
  );
};
